import { useEffect, useRef, useState } from "react";
import { Row, RowState } from "./Row";
import { hotclue, Clue, CluedLetter } from "./clue";
import { Keyboard } from "./Keyboard";
import { Thermometer } from "./Thermometer";
import { longPollGameState, RemoteGameState } from "./gameStateApi";
import { GameState, Puzzle } from "./Game";
import { hotClueDistance } from "./util";

interface WatchViewProps {
  puzzleId: string;
  puzzle: Puzzle;
  colorBlind: boolean;
  keyboardLayout: string;
  maxGuesses: number;
  targetUserId?: string;
  watchLabel: string;
  children?: React.ReactNode;
  onStatusChange?: (status: "in_progress" | "won" | "lost") => void;
}

export function WatchView(props: WatchViewProps) {
  const { puzzleId, puzzle, maxGuesses, keyboardLayout, targetUserId, watchLabel } = props;
  const [remote, setRemote] = useState<RemoteGameState | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!targetUserId) return;

    let active = true;

    async function longPollLoop(userId: string, after: string | null) {
      while (active) {
        const controller = new AbortController();
        abortRef.current = controller;
        const data = await longPollGameState(userId, puzzleId, after, controller.signal);
        if (!active) return;
        if (data) {
          setRemote(data);
          after = data.updated_at || null;
        }
      }
    }

    // Initial fetch is just a long-poll with after=null
    longPollLoop(targetUserId, null);

    return () => {
      active = false;
      abortRef.current?.abort();
    };
  }, [puzzleId, targetUserId]);

  // Notify parent of status changes
  useEffect(() => {
    if (remote && props.onStatusChange) {
      props.onStatusChange(remote.metadata.status);
    }
  }, [remote?.metadata?.status]);

  const guessWords = (remote?.state.guesses ?? []).map((g) => g.word);
  const currentGuess = remote?.state.currentGuess ?? "     ";
  const cursorPosition = remote?.state.cursorPosition ?? 0;
  const status = remote?.metadata?.status ?? "in_progress";
  const isLive = status === "in_progress";
  const gameOver = status === "won" || status === "lost";

  const getLetterInfo = (): Map<string, Clue> => {
    const letterInfo = new Map<string, Clue>();
    for (const word of guessWords) {
      const clued = hotclue(word, puzzle.target);
      for (let col = 0; col < 5; col++) {
        const { clue, letter } = clued[col];
        if (clue === undefined) continue;

        if (clue === Clue.Correct) {
          letterInfo.set(letter, Clue.Correct);
          for (let c = "a".charCodeAt(0); c <= "z".charCodeAt(0); c++) {
            if (c !== letter.charCodeAt(0)) {
              const ch = String.fromCharCode(c);
              if (!letterInfo.has(ch) || letterInfo.get(ch) !== Clue.Correct) {
                if (Math.abs(letter.charCodeAt(0) - c) <= hotClueDistance) {
                  letterInfo.set(ch, Clue.Absent);
                }
              }
            }
          }
        } else if (clue === Clue.Elsewhere) {
          const old = letterInfo.get(letter);
          if (old !== Clue.Correct) {
            letterInfo.set(letter, Clue.Elsewhere);
          }
        } else if (clue === Clue.Absent) {
          const old = letterInfo.get(letter);
          if (old === undefined) {
            letterInfo.set(letter, Clue.Absent);
          }
        }
      }
    }
    return letterInfo;
  };

  const letterInfo = getLetterInfo();

  // Per-column letter info for Thermometer (same logic as Game.tsx)
  const getCluedColumn = (col: number): CluedLetter[] => {
    return guessWords.map((word) => hotclue(word, puzzle.target)[col]);
  };

  const getColumnLetterInfo = (cluedColumn: CluedLetter[]): Map<string, Clue> => {
    const info = new Map<string, Clue>();
    for (const { clue, letter } of cluedColumn) {
      if (clue === undefined) continue;

      if (clue === Clue.Absent || clue === Clue.Correct) {
        info.set(letter, clue);
      } else if (clue === Clue.Elsewhere) {
        info.set(letter, Clue.Absent);
      }

      const proximityLetters: string[] = [];
      const nonProximityLetters: string[] = [];
      for (let charCode = "a".charCodeAt(0); charCode <= "z".charCodeAt(0); ++charCode) {
        const distance = letter.charCodeAt(0) - charCode;
        if (distance === 0) continue;
        if (Math.abs(distance) > hotClueDistance) {
          nonProximityLetters.push(String.fromCharCode(charCode));
        } else {
          proximityLetters.push(String.fromCharCode(charCode));
        }
      }

      if (clue === Clue.Absent) {
        for (const l of proximityLetters) info.set(l, Clue.Absent);
      }
      if (clue === Clue.Elsewhere) {
        for (const l of proximityLetters) {
          if (info.get(l) === undefined) info.set(l, Clue.Elsewhere);
        }
        for (const l of nonProximityLetters) info.set(l, Clue.Absent);
      }
      if (clue === Clue.Correct) {
        for (const l of proximityLetters) info.set(l, Clue.Absent);
        for (const l of nonProximityLetters) info.set(l, Clue.Absent);
      }
    }
    return info;
  };

  const letterInfos = [0, 1, 2, 3, 4].map((col) => getColumnLetterInfo(getCluedColumn(col)));

  const won = status === "won";
  const lost = status === "lost";
  const watchGameState = won ? GameState.Won : lost ? GameState.Lost : GameState.Playing;

  const gameOverText = won
    ? "They won!"
    : lost
    ? `They lost! The answer was ${puzzle.target.toUpperCase()}.`
    : null;

  const tableRows = Array(maxGuesses)
    .fill(undefined)
    .map((_, i) => {
      const guess = [...guessWords, currentGuess][i] ?? "";
      const cluedLetters = hotclue(guess, puzzle.target);
      const lockedIn = i < guessWords.length;
      const isEditing = !gameOver && i === guessWords.length;
      return (
        <Row
          key={i}
          currentGuess={[currentGuess, cursorPosition]}
          onClick={() => {}}
          rowState={
            lockedIn
              ? RowState.LockedIn
              : isEditing
              ? RowState.Editing
              : RowState.Pending
          }
          cluedLetters={cluedLetters}
          cluedColumn={[]}
          annotation={"\u00a0"}
        />
      );
    });

  const bannerClass = isLive ? "watch-banner watch-banner--live" : "watch-banner";
  const borderClass = isLive ? "watch-border watch-border--live" : "watch-border";

  return (
    <div className="Game">
      <div className="watch-banner-wrapper">
        <div className={bannerClass}>
          {isLive && <span className="watch-dot" />}
          {watchLabel}
        </div>
      </div>
      <div className={borderClass}>
        <table
          className="Game-rows"
          tabIndex={0}
          aria-label="watched game board"
        >
          <tbody>{tableRows}</tbody>
        </table>
      </div>
      {gameOverText && (
        <p role="alert" style={{ whiteSpace: "pre-wrap" }}>
          {gameOverText}
        </p>
      )}
      {props.children}
      <Keyboard
        layout={keyboardLayout}
        letterInfo={letterInfo}
        onKey={() => {}}
      />
      <Thermometer letterInfos={letterInfos} gameState={watchGameState} />
    </div>
  );
}

export default WatchView;
